// src/modules/butterfly-meadow/layout.ts
/**
 * The butterfly art area: 1024×512 px, body on the vertical centre line.
 * 1 world unit = 512 px, sheet y = 256 is z = 0 and the top of the sheet is the
 * head (+z). Wing paths describe the LEFT side; the right side is the same
 * path mirrored around x = 512, but the child colours both sides independently.
 */
export const ART_WIDTH = 1024;
export const ART_HEIGHT = 512;
export const PX_PER_UNIT = 512;

export function artToLocal(px: number, py: number): { x: number; z: number } {
  return { x: (px - ART_WIDTH / 2) / PX_PER_UNIT, z: (ART_HEIGHT / 2 - py) / PX_PER_UNIT };
}

/** Matches `body-shape.ts`: head z≈0.21, thorax z 0…0.17, abdomen z −0.4…0. */
export const BODY_LAYOUT = {
  head: { cx: 512, cy: 148, r: 23 },
  thorax: { cx: 512, cy: 212, rx: 30, ry: 44 },
  abdomen: { cx: 512, cy: 361, rx: 24, ry: 100 },
} as const;

/** Три печатные формы крыльев: контуры известны заранее (`WING_SHAPES`). */
export type PrintedShapeId = 'classic' | 'swallowtail' | 'moth';
/** «Своя бабочка»: крылья любой формы, их контур — это прозрачность самого рисунка (art), а не `WING_SHAPES`. */
export const FREEFORM_SHAPE_ID = 'freeform' as const;
export type WingShapeId = PrintedShapeId | typeof FREEFORM_SHAPE_ID;
export const CUSTOM_WING_SHAPE_IDS = ['classic', 'swallowtail', 'moth'] as const;
export const READY_SPECIES_SHAPE_IDS = [
  'blue', 'brimstone', 'monarch', 'peacock', 'machaon', 'luna', 'morpho', 'cabbage',
  'tortoiseshell', 'admiral', 'painted', 'glasswing', 'zebra', 'birdwing', 'copper', 'sunset',
] as const;
export type ReadySpeciesShapeId = typeof READY_SPECIES_SHAPE_IDS[number];
/** All contour-based shapes used by the meadow renderer; freeform uses its art alpha instead. */
export type MeadowContourShapeId = PrintedShapeId | ReadySpeciesShapeId;
export type MeadowShapeId = WingShapeId | ReadySpeciesShapeId;
export type WingShape = { label: string; fore: string; hind: string; eye: [number, number] };

export const WING_SHAPES: Record<MeadowContourShapeId, WingShape> = {
  classic: {
    label: 'Классическая',
    fore: 'M492 205 C430 150 300 40 120 45 C60 48 30 90 45 140 C70 215 160 250 260 262 C360 272 440 262 492 260 Z',
    hind: 'M492 258 C420 262 250 275 170 330 C110 372 120 450 190 470 C280 495 400 420 492 300 Z',
    eye: [270, 400],
  },
  swallowtail: {
    label: 'Парусник',
    fore: 'M492 205 C430 140 260 30 70 40 C40 45 35 70 55 100 C110 190 200 245 300 262 C390 272 450 265 492 260 Z',
    hind: 'M492 258 C410 268 260 290 210 350 C180 390 200 430 250 440 C268 468 278 500 298 506 C320 506 322 470 332 445 C400 420 460 360 492 300 Z',
    eye: [300, 380],
  },
  moth: {
    label: 'Круглая',
    fore: 'M492 210 C420 120 230 60 110 90 C40 110 40 200 110 250 C200 280 400 270 492 262 Z',
    hind: 'M492 258 C400 270 200 300 160 370 C130 430 220 470 320 440 C420 410 480 340 492 300 Z',
    eye: [290, 395],
  },
  blue: {
    label: 'Голубянка',
    fore: 'M492 205 C420 160 310 75 200 90 C120 105 105 185 180 230 C260 270 400 270 492 260 Z',
    hind: 'M492 258 C395 270 260 278 225 340 C190 405 260 455 335 425 C415 390 465 340 492 300 Z',
    eye: [310, 365],
  },
  brimstone: {
    label: 'Лимонница',
    fore: 'M492 205 C405 135 255 70 92 36 C100 112 78 154 145 205 C255 266 395 272 492 260 Z',
    hind: 'M492 258 C395 265 255 290 185 350 C210 375 205 420 260 462 C365 445 450 365 492 300 Z',
    eye: [300, 365],
  },
  monarch: {
    label: 'Монарх',
    fore: 'M492 205 C400 125 215 12 100 45 C50 60 60 120 115 175 C215 255 375 275 492 260 Z',
    hind: 'M492 258 C400 270 225 280 175 355 C145 408 190 468 260 465 C365 462 455 365 492 300 Z',
    eye: [285, 385],
  },
  peacock: {
    label: 'Павлиний глаз',
    fore: 'M492 205 C410 135 240 45 100 55 L120 90 L65 118 L100 150 L78 182 C180 258 380 270 492 260 Z',
    hind: 'M492 258 C395 270 235 282 170 345 L190 375 L173 405 L220 418 L238 456 C345 460 455 365 492 300 Z',
    eye: [290, 370],
  },
  machaon: {
    label: 'Махаон',
    fore: 'M492 205 C400 120 240 30 55 32 C40 65 95 142 155 190 C265 260 395 275 492 260 Z',
    hind: 'M492 258 C385 275 255 290 200 350 C180 385 205 413 250 425 C255 455 264 493 280 505 C297 498 300 457 310 438 C400 410 460 355 492 300 Z',
    eye: [285, 380],
  },
  luna: {
    label: 'Лунная бабочка',
    fore: 'M492 205 C415 120 205 42 52 80 C70 130 95 220 175 252 C290 285 410 272 492 260 Z',
    hind: 'M492 258 C385 270 240 285 215 335 C185 385 240 412 282 395 C300 430 238 485 208 505 C280 505 350 450 355 398 C420 370 468 330 492 300 Z',
    eye: [300, 345],
  },
  morpho: {
    label: 'Морфо',
    fore: 'M492 205 C395 100 195 30 85 65 C25 95 55 170 135 215 C250 275 400 272 492 260 Z',
    hind: 'M492 258 C380 265 195 285 150 355 C120 430 200 480 290 453 C390 420 455 355 492 300 Z',
    eye: [285, 380],
  },
  cabbage: {
    label: 'Белянка',
    fore: 'M492 205 C410 155 290 55 175 70 C100 80 85 140 140 195 C230 260 395 267 492 260 Z',
    hind: 'M492 258 C400 270 265 290 230 355 C200 410 255 450 320 425 C410 390 460 340 492 300 Z',
    eye: [315, 370],
  },
  tortoiseshell: {
    label: 'Крапивница',
    fore: 'M492 205 C400 125 265 60 120 55 L135 90 L85 115 L115 150 L100 180 C215 255 395 270 492 260 Z',
    hind: 'M492 258 C400 265 260 282 205 340 L225 365 L200 390 L245 410 L255 447 C350 435 450 355 492 300 Z',
    eye: [310, 365],
  },
  admiral: {
    label: 'Адмирал',
    fore: 'M492 205 C395 130 235 25 95 48 C55 63 65 118 120 165 L100 180 C215 250 390 270 492 260 Z',
    hind: 'M492 258 C380 270 225 290 185 355 C160 405 205 450 265 452 L282 435 L307 447 C395 410 455 350 492 300 Z',
    eye: [285, 380],
  },
  painted: {
    label: 'Репейница',
    fore: 'M492 205 C395 130 240 48 100 30 L120 90 C65 110 95 165 155 200 C260 262 405 270 492 260 Z',
    hind: 'M492 258 C385 272 250 285 205 340 C165 395 210 455 275 450 C365 435 455 355 492 300 Z',
    eye: [295, 380],
  },
  glasswing: {
    label: 'Стеклянница',
    fore: 'M492 205 C415 160 290 50 210 55 C170 65 160 120 210 177 C290 252 410 270 492 260 Z',
    hind: 'M492 258 C410 270 295 300 280 355 C265 410 305 440 355 405 C420 365 465 330 492 300 Z',
    eye: [340, 360],
  },
  zebra: {
    label: 'Зебра',
    fore: 'M492 205 C365 145 175 30 65 60 C30 80 55 125 125 155 C245 220 400 262 492 260 Z',
    hind: 'M492 258 C370 280 220 320 190 380 C180 420 230 440 285 405 C375 365 445 335 492 300 Z',
    eye: [280, 370],
  },
  birdwing: {
    label: 'Птицекрылка',
    fore: 'M492 205 C370 110 170 20 40 50 C30 90 100 165 190 205 C290 255 405 270 492 260 Z',
    hind: 'M492 258 C380 275 215 300 170 365 C150 420 225 465 295 440 C390 410 450 350 492 300 Z',
    eye: [295, 380],
  },
  copper: {
    label: 'Червонец',
    fore: 'M492 205 C415 145 315 85 220 100 C160 110 150 160 200 205 C285 260 410 270 492 260 Z',
    hind: 'M492 258 C405 275 295 295 270 345 C240 395 280 430 335 405 C410 380 465 335 492 300 Z',
    eye: [330, 360],
  },
  sunset: {
    label: 'Урания',
    fore: 'M492 205 C385 125 215 35 70 55 C50 90 115 160 175 195 C285 258 405 275 492 260 Z',
    hind: 'M492 258 C385 275 245 295 205 350 L220 380 L195 445 L245 415 L260 490 L290 430 L325 480 L335 415 C415 385 465 335 492 300 Z',
    eye: [290, 380],
  },
};

/** Печатные формы (порядок важен: от него зависят коды маркеров листа). */
export const WING_SHAPE_IDS: PrintedShapeId[] = [...CUSTOM_WING_SHAPE_IDS];
/** Все формы, которые знает мир бабочек: печатные и «Своя бабочка». */
export const ALL_SHAPE_IDS: readonly WingShapeId[] = [...WING_SHAPE_IDS, FREEFORM_SHAPE_ID];
export const isFreeformShape = (shape: MeadowShapeId): shape is typeof FREEFORM_SHAPE_ID => shape === FREEFORM_SHAPE_ID;
export const SHAPE_LABELS: Record<MeadowShapeId, string> = {
  classic: WING_SHAPES.classic.label, swallowtail: WING_SHAPES.swallowtail.label, moth: WING_SHAPES.moth.label, freeform: 'Своя бабочка',
  blue: 'Голубянка', brimstone: 'Лимонница', monarch: 'Монарх', peacock: 'Павлиний глаз',
  machaon: 'Махаон', luna: 'Лунная бабочка', morpho: 'Морфо', cabbage: 'Белянка',
  tortoiseshell: 'Крапивница', admiral: 'Адмирал', painted: 'Репейница', glasswing: 'Стеклянница',
  zebra: 'Зебра', birdwing: 'Птицекрылка', copper: 'Червонец', sunset: 'Урания',
};
